import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { BadgeCheck, MessageCircle, Phone, Home as HomeIcon, Eye, Key, Tag, ChevronLeft } from "lucide-react";
import { api } from "../lib/api";
import { formatPrice, findLabelByValue } from "../lib/constants";

const ROLE_LABELS = { particulier: "Particulier", agence: "Agence immobilière", promoteur: "Promoteur", demarcheur: "Démarcheur", admin: "IMORA Tchad" };

export default function AgentProfile() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get(`/agents/${userId}`).then(({ data }) => setData(data)).catch(() => setError(true));
  }, [userId]);

  if (error) return <div className="max-w-md mx-auto p-10 text-center text-neutral-500">Profil introuvable.</div>;
  if (!data) return <div className="max-w-md mx-auto p-10 text-center text-neutral-500">Chargement…</div>;

  const { user, stats, properties } = data;
  const displayName = user.agency_name || user.name;
  const wa = (user.whatsapp || user.phone || "").replace(/[^0-9]/g, "");
  const since = user.created_at ? new Date(user.created_at).getFullYear() : null;
  const shown = filter === "all" ? properties : properties.filter((p) => p.status === filter);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <Link to="/search" className="text-sm font-bold text-neutral-500 hover:text-neutral-900 mb-4 inline-flex items-center gap-1"><ChevronLeft className="h-4 w-4" /> Recherche</Link>

      {/* Header card */}
      <div className="bg-[#0A0A0A] text-white rounded-2xl p-6 sm:p-8 mb-6" data-testid="agent-header">
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          {user.picture ? (
            <img src={user.picture} alt={displayName} className="h-20 w-20 rounded-2xl object-cover border-2 border-white/20" />
          ) : (
            <div className="h-20 w-20 rounded-2xl bg-[#FF6B1A] flex items-center justify-center font-heading font-black text-3xl">{displayName?.[0] || "A"}</div>
          )}
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-heading font-black text-2xl sm:text-3xl tracking-tighter" data-testid="agent-name">{displayName}</h1>
              {user.verified_agency && (
                <span className="inline-flex items-center gap-1 bg-[#00B4FF] text-white text-xs font-bold px-2.5 py-1 rounded-full" data-testid="agent-verified-badge">
                  <BadgeCheck className="h-3.5 w-3.5" /> Vérifié
                </span>
              )}
            </div>
            <p className="text-white/60 text-sm mt-1">
              {ROLE_LABELS[user.role] || user.role}{user.agency_name && user.name !== user.agency_name ? ` · ${user.name}` : ""}{since ? ` · Membre depuis ${since}` : ""}
            </p>
            {user.bio && <p className="text-white/80 text-sm mt-3 max-w-2xl whitespace-pre-wrap" data-testid="agent-bio">{user.bio}</p>}
          </div>
          <div className="flex sm:flex-col gap-2">
            {wa && (
              <a href={`https://wa.me/${wa}?text=${encodeURIComponent(`Bonjour ${displayName}, je vous contacte via votre page IMORA Tchad.`)}`} target="_blank" rel="noopener noreferrer" data-testid="agent-whatsapp-btn" className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EBE5A] text-white font-bold text-sm px-5 h-11 rounded-lg transition">
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
            )}
            {user.phone && (
              <a href={`tel:${user.phone}`} data-testid="agent-call-btn" className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold text-sm px-5 h-11 rounded-lg transition">
                <Phone className="h-4 w-4" /> Appeler
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        <StatCard icon={HomeIcon} label="Annonces en ligne" value={stats.active} color="#FF6B1A" testid="agent-stat-active" />
        <StatCard icon={Tag} label="Biens vendus" value={stats.sold} color="#22C55E" testid="agent-stat-sold" />
        <StatCard icon={Key} label="Biens loués" value={stats.rented} color="#00B4FF" testid="agent-stat-rented" />
        <StatCard icon={Eye} label="Vues cumulées" value={stats.views} color="#0A0A0A" testid="agent-stat-views" />
      </div>

      {/* Listings */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-black text-2xl tracking-tighter">Ses annonces</h2>
        <div className="flex gap-1.5">
          {[["all", "Toutes"], ["active", "En ligne"], ["sold", "Vendues"], ["rented", "Louées"]].map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)} data-testid={`agent-filter-${v}`} className={`text-xs font-bold px-3 py-1.5 rounded-full ${filter === v ? "bg-[#0A0A0A] text-white" : "bg-white border border-neutral-200"}`}>{l}</button>
          ))}
        </div>
      </div>
      {shown.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-xl p-10 text-center text-neutral-500">Aucune annonce dans cette catégorie</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {shown.map((p) => (
            <Link key={p.id} to={`/property/${p.id}`} data-testid={`agent-prop-${p.id}`} className="imora-card bg-white border border-neutral-200 rounded-xl overflow-hidden hover:shadow-lg transition">
              <div className="relative aspect-[4/3]">
                <img src={p.photos?.[0] || "https://images.unsplash.com/photo-1706164971302-e30c0640cc3b?w=600&q=60"} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                {p.status !== "active" && (
                  <span className={`absolute top-2 left-2 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full text-white ${p.status === "sold" ? "bg-[#FF6B1A]" : "bg-[#00B4FF]"}`}>
                    {p.status === "sold" ? "🏠 Vendu" : "🔑 Loué"}
                  </span>
                )}
                {p.verified && <span className="absolute top-2 right-2 bg-[#00B4FF] text-white text-[10px] font-bold px-2 py-1 rounded-full">✓ Vérifié</span>}
              </div>
              <div className="p-3">
                <div className="font-heading font-bold line-clamp-1">{p.title}</div>
                <div className="text-xs text-neutral-500">{findLabelByValue(p.transaction_type)} · {p.neighborhood}, {p.city}</div>
                <div className="text-[#FF6B1A] font-heading font-black mt-1">{formatPrice(p.price)}</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, color, testid }) => (
  <div className="bg-white border border-neutral-200 rounded-xl p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">{label}</div>
      <Icon className="h-4 w-4" style={{ color }} />
    </div>
    <div className="font-heading font-black text-2xl mt-1" style={{ color }}>{value}</div>
  </div>
);
